import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "./chart";
import type { ChartConfig } from "./chart";
import { cn } from "../lib/utils";

const DEFAULT_COLORS = ["#655560", "#1564BF", "#405F90", "#8884d8", "#82ca9d", "#ffc658"];

export interface ChartLineConfig {
  key: string;
  color?: string;
  label?: string;
}

export interface ChartLineProps {
  data: Array<Record<string, unknown>>;
  xAxisKey: string;
  lines: ChartLineConfig[];
  className?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  height?: number;
  formatXAxisAsDate?: boolean;
}

export const ChartLine = ({
  data,
  xAxisKey,
  lines,
  className,
  showLegend = true,
  showGrid = true,
  height = 300,
  formatXAxisAsDate = true,
}: ChartLineProps) => {
  const chartConfig = useMemo(() => {
    return lines.reduce<ChartConfig>((acc, line, index) => {
      acc[line.key] = {
        label: line.label ?? line.key.charAt(0).toUpperCase() + line.key.slice(1),
        color: line.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      };
      return acc;
    }, {});
  }, [lines]);

  const sortedData = useMemo(() => {
    if (formatXAxisAsDate) {
      return [...data].sort((a, b) => {
        const dateA = new Date(a[xAxisKey] as string).getTime();
        const dateB = new Date(b[xAxisKey] as string).getTime();
        return dateA - dateB;
      });
    }
    return data;
  }, [data, xAxisKey, formatXAxisAsDate]);

  const formatXAxis = (value: unknown) => {
    if (formatXAxisAsDate && typeof value === "string") {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }
    }
    return String(value);
  };

  return (
    <ChartContainer config={chartConfig} className={cn("aspect-auto w-full", className)} style={{ height }}>
      <LineChart data={sortedData}>
        {showGrid && <CartesianGrid vertical={false} />}
        <XAxis
          dataKey={xAxisKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={formatXAxis}
        />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              labelFormatter={(value: string | number) => {
                if (formatXAxisAsDate && typeof value === "string") {
                  const date = new Date(value);
                  if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    });
                  }
                }
                return String(value);
              }}
              indicator="dot"
            />
          }
        />
        {lines.map((line) => (
          <Line
            key={line.key}
            dataKey={line.key}
            type="monotone"
            stroke={`var(--color-${line.key})`}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      </LineChart>
    </ChartContainer>
  );
};
